import {
  CSSProperties,
  ReactNode,
  useState,
  useSyncExternalStore
} from 'react';
import { useRhinoContext } from '@rhino-project/core';
import env from '@rhino-project/core/config/env';
import { RhinoDevContext } from '@rhino-project/core/hooks';

export type RhinoDevToolTheme = 'light' | 'dark';

// The panel renders inside the TanStack devtools shell, which themes itself
// independently of the host app — so no app Tailwind/HeroUI classes here.
// Colors come from the @tanstack/devtools-ui token palette ([light, dark])
// so the Rhino tab reads as a native devtools panel in both shell themes.
const PALETTE = {
  text: ['#344054', '#d0d5dd'],
  heading: ['#101828', '#f2f4f7'],
  muted: ['#667085', '#98a2b3'],
  key: ['#1570EF', '#84CAFF'],
  string: ['#039855', '#32D583'],
  number: ['#DC6803', '#FDB022'],
  boolean: ['#be185d', '#ec4899'],
  func: ['#0e7490', '#22d3ee'],
  guide: ['#B2DDFF', '#313749'],
  cardBg: ['#f9fafb', '#111318'],
  cardBorder: ['#eaecf0', '#1d2939'],
  tagBg: ['#EFF8FF', '#1849A9'],
  tagText: ['#175CD3', '#D1E9FF']
} as const;

type Colors = Record<keyof typeof PALETTE, string>;

const themeColors = (theme: RhinoDevToolTheme): Colors =>
  Object.fromEntries(
    Object.entries(PALETTE).map(([name, [light, dark]]) => [
      name,
      theme === 'dark' ? dark : light
    ])
  ) as Colors;

const MONO_FONT =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";

const Chevron = ({ open, color }: { open: boolean; color: string }) => (
  <svg
    viewBox="0 0 16 16"
    width={10}
    height={10}
    style={{
      flex: 'none',
      transition: 'transform 0.1s ease',
      transform: open ? 'rotate(90deg)' : 'none'
    }}
  >
    <path
      d="M6 4l4 4-4 4"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const isExpandable = (value: unknown): value is object =>
  typeof value === 'object' && value !== null && !(value instanceof Date);

const entriesOf = (value: object): [string, unknown][] =>
  Array.isArray(value)
    ? value.map((item, index): [string, unknown] => [String(index), item])
    : Object.entries(value);

const Primitive = ({ value, colors }: { value: unknown; colors: Colors }) => {
  const style = (color: string, italic = false): CSSProperties => ({
    color,
    fontStyle: italic ? 'italic' : undefined,
    wordBreak: 'break-all',
    whiteSpace: 'pre-wrap'
  });

  if (value === null)
    return <span style={style(colors.muted, true)}>null</span>;
  if (value === undefined)
    return <span style={style(colors.muted, true)}>undefined</span>;

  switch (typeof value) {
    case 'string':
      return <span style={style(colors.string)}>&quot;{value}&quot;</span>;
    case 'number':
    case 'bigint':
      return <span style={style(colors.number)}>{String(value)}</span>;
    case 'boolean':
      return <span style={style(colors.boolean)}>{String(value)}</span>;
    case 'function':
      return (
        <span style={style(colors.func, true)}>
          ƒ {(value as { name?: string }).name || ''}()
        </span>
      );
    default:
      return (
        <span style={style(colors.muted)}>
          {value instanceof Date ? value.toISOString() : String(value)}
        </span>
      );
  }
};

const JsonNode = ({
  name,
  value,
  depth,
  expandDepth,
  colors
}: {
  name?: string;
  value: unknown;
  depth: number;
  expandDepth: number;
  colors: Colors;
}) => {
  const [open, setOpen] = useState(depth < expandDepth);

  const label = name !== undefined && (
    <span style={{ color: colors.key }}>{name}: </span>
  );

  if (!isExpandable(value)) {
    return (
      <div style={{ paddingLeft: 14 }}>
        {label}
        <Primitive value={value} colors={colors} />
      </div>
    );
  }

  const entries = entriesOf(value);
  const braces = Array.isArray(value) ? '[…]' : '{…}';
  const count = `${entries.length} ${Array.isArray(value) ? 'items' : 'keys'}`;

  if (entries.length === 0) {
    return (
      <div style={{ paddingLeft: 14 }}>
        {label}
        <span style={{ color: colors.muted }}>
          {Array.isArray(value) ? '[]' : '{}'}
        </span>
      </div>
    );
  }

  return (
    <div>
      <div
        role="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <Chevron open={open} color={colors.key} />
        {label}
        <span style={{ color: colors.muted, fontStyle: 'italic' }}>
          {open ? count : `${braces} ${count}`}
        </span>
      </div>
      {open && (
        <div
          style={{
            marginLeft: 4,
            paddingLeft: 10,
            borderLeft: `1px solid ${colors.guide}`
          }}
        >
          {entries.map(([key, child]) => (
            <JsonNode
              key={key}
              name={key}
              value={child}
              depth={depth + 1}
              expandDepth={expandDepth}
              colors={colors}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const JsonTree = ({
  value,
  colors,
  expandDepth = 1
}: {
  value: unknown;
  colors: Colors;
  expandDepth?: number;
}) => (
  <div
    style={{
      fontFamily: MONO_FONT,
      fontSize: 11,
      lineHeight: 1.6,
      color: colors.text
    }}
  >
    <JsonNode
      value={value}
      depth={0}
      expandDepth={expandDepth}
      colors={colors}
    />
  </div>
);

const Card = ({
  title,
  tag,
  colors,
  children
}: {
  title: string;
  tag?: string;
  colors: Colors;
  children: ReactNode;
}) => (
  <section
    style={{
      flex: 'none',
      background: colors.cardBg,
      border: `1px solid ${colors.cardBorder}`,
      borderRadius: 8,
      overflow: 'hidden'
    }}
  >
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '6px 12px',
        borderBottom: `1px solid ${colors.cardBorder}`
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 13, color: colors.heading }}>
        {title}
      </span>
      {tag && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            padding: '1px 8px',
            borderRadius: 9999,
            background: colors.tagBg,
            color: colors.tagText
          }}
        >
          {tag}
        </span>
      )}
    </header>
    <div style={{ padding: '8px 12px' }}>{children}</div>
  </section>
);

const ContextCard = ({
  context: { type, context },
  colors
}: {
  context: RhinoDevContext;
  colors: Colors;
}) => {
  const model = (context as { model?: { readableName?: string } })?.model;

  return (
    <Card
      title={model?.readableName ?? type}
      tag={model && type}
      colors={colors}
    >
      <JsonTree value={context} colors={colors} />
    </Card>
  );
};

export const RhinoDevToolPanel = ({
  theme = 'dark'
}: {
  theme?: RhinoDevToolTheme;
}) => {
  const { devtoolsEventClient } = useRhinoContext();
  const contexts = useSyncExternalStore(
    devtoolsEventClient.subscribe,
    devtoolsEventClient.getSnapshot,
    devtoolsEventClient.getSnapshot
  );
  const colors = themeColors(theme);

  const ids = Object.keys(contexts);

  // The devtools shell forces `height: 100%` onto every direct child of the
  // plugin root (`.<plugin-container> > * > *`), expecting a single full-height
  // child. So the outer div is just that single child: it owns the scroll and
  // padding, while the actual content flows one level deeper where the shell
  // rule no longer reaches. A flat layout would clamp each section to one panel
  // height and let the tall context overflow on top of the others.
  return (
    <div style={{ height: '100%' }}>
      <div
        style={{
          height: '100%',
          overflow: 'auto',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: 12,
          fontFamily: 'ui-sans-serif, Inter, system-ui, sans-serif',
          color: colors.text
        }}
      >
        {/* Live model context first — it's the reason to open this panel. */}
        {ids.length === 0 && (
          <div
            style={{
              color: colors.muted,
              fontSize: 12,
              textAlign: 'center',
              padding: '24px 12px'
            }}
          >
            No active model context — open a model page to see it here.
          </div>
        )}
        {ids.map((id) => (
          <ContextCard key={id} context={contexts[id]} colors={colors} />
        ))}
        <Card title="Env" colors={colors}>
          <JsonTree value={env} colors={colors} expandDepth={0} />
        </Card>
      </div>
    </div>
  );
};
